import { z } from 'zod'

import type {
  BookingCategory,
  BookingQuestion,
  BookingType,
} from '@/api/booking-types/booking-types.types'
import { sortByOrder } from '@/api/booking-types/booking-types.utils'

export const BookingSelectionSchema = z.object({
  groupId: z.number().nullable(),
  questionId: z.number().nullable(),
  typeId: z.number().nullable(),
})

export type BookingSelection = z.infer<typeof BookingSelectionSchema>

export const EMPTY_SELECTION: BookingSelection = { groupId: null, questionId: null, typeId: null }

export const parseStoredSelection = (value: unknown): BookingSelection => {
  const result = BookingSelectionSchema.safeParse(value)
  return result.success ? result.data : EMPTY_SELECTION
}

interface ResolveSelectionParams {
  groups: BookingCategory[]
  questions: BookingQuestion[]
  stored: BookingSelection
  types: BookingType[]
}

const pickId = <T extends { id: number }>(items: T[], preferredId: number | null) =>
  items.find(({ id }) => id === preferredId)?.id ?? items[0]?.id ?? null

// Note: a stored id that no longer exists (deleted item, other environment) falls back to the first item of its level
export const resolveSelection = ({
  groups,
  questions,
  stored,
  types,
}: ResolveSelectionParams): BookingSelection => {
  const groupId = pickId(sortByOrder(groups), stored.groupId)

  const groupTypes = sortByOrder(types.filter(({ category }) => category === groupId))
  const typeId = pickId(groupTypes, stored.typeId)

  const typeQuestions = sortByOrder(
    questions.filter(({ bookingTypeId }) => bookingTypeId?.id === typeId),
  )
  const questionId = pickId(typeQuestions, stored.questionId)

  return { groupId, questionId, typeId }
}
